export const getPropertyStatus = (property = {}) =>
  String(property?.status || property?.Status || "").trim();

export const getPropertyZipCode = (property = {}) => {
  const address =
    property?.parkingLotAddress ??
    property?.ParkingLotAddress ??
    property?.parkingLotMailingAdderess ??
    property?.ParkingLotMailingAdderess ??
    property?.streetName ??
    property?.StreetName ??
    "";

  const addressZip = String(address).match(/\b(\d{5})(?:-\d{4})?\b/);
  if (addressZip) {
    return addressZip[1];
  }

  const explicit =
    property?.zipcode ??
    property?.zipCode ??
    property?.Zipcode ??
    property?.ZipCode;

  if (explicit !== undefined && explicit !== null && String(explicit).trim() !== "") {
    return String(explicit).trim();
  }

  return "";
};

export const getEntityAddressZipCode = (entity = {}) => {
  const address =
    entity?.enityMailingAdderess ??
    entity?.EnityMailingAdderess ??
    entity?.entityAddress ??
    entity?.EntityAddress ??
    "";

  const match = String(address).match(/\b(\d{5})(?:-\d{4})?\b/);
  if (match) {
    return match[1];
  }

  const explicit =
    entity?.zipcode ??
    entity?.zipCode ??
    entity?.Zipcode ??
    entity?.ZipCode;

  if (explicit !== undefined && explicit !== null && String(explicit).trim() !== "") {
    return String(explicit).trim();
  }

  return "";
};

export const getParkingLotId = (property = {}) => {
  const id =
    property?.parking_Lot_ID ??
    property?.Parking_Lot_ID ??
    property?.parkingLotID ??
    property?.parkingLotId ??
    property?.ParkingLotID ??
    property?.ParkingLotId ??
    property?.value;

  const numericId = Number(id);
  return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
};

export const getEntityRegistrationId = (property = {}) => {
  const id =
    property?.entityRegistrationID ??
    property?.entityRegistrationId ??
    property?.EntityRegistrationID ??
    property?.EntityRegistrationId;

  const numericId = Number(id);
  return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
};

export const isPricingPlanAssignable = (property = {}) =>
  getPropertyStatus(property).toLowerCase() === "active";

export const filterAssignableProperties = (properties = []) =>
  (Array.isArray(properties) ? properties : []).filter(isPricingPlanAssignable);

export const flattenParkingLotRecords = (data) => {
  const items = Array.isArray(data)
    ? data
    : (data?.parkingLots || data?.properties || data?.data || []);

  if (!Array.isArray(items)) return [];

  return items
    .map((item) => {
      if (!item || typeof item !== "object") return null;

      if (item.pl && typeof item.pl === "object") {
        return {
          ...item,
          ...item.pl,
          status: getPropertyStatus(item) || getPropertyStatus(item.pl) || "Active",
        };
      }

      return item;
    })
    .filter(Boolean);
};

export const normalizePropertyForPricingAssignment = (property = {}, { defaultStatus } = {}) => {
  const zipcode = getPropertyZipCode(property);
  const status = getPropertyStatus(property) || (defaultStatus ? String(defaultStatus).trim() : "");

  return {
    ...property,
    status,
    zipcode,
    zipCode: zipcode,
    Zipcode: zipcode,
    ZipCode: zipcode,
  };
};

export const mergePropertyRecords = (...groups) => {
  const mergedByKey = new Map();

  const getRecordKey = (record) => {
    const lotId = getParkingLotId(record);
    if (lotId) return `lot:${lotId}`;

    const nickName = String(record?.nickName || record?.NickName || "").trim().toLowerCase();
    const address = String(
      record?.parkingLotAddress ||
      record?.ParkingLotAddress ||
      record?.parkingLotMailingAdderess ||
      ""
    ).trim().toLowerCase();

    if (nickName && address) return `name:${nickName}|${address}`;
    if (nickName) return `name:${nickName}`;
    return null;
  };

  const addRecord = (record) => {
    const normalized = normalizePropertyForPricingAssignment(record);
    const key = getRecordKey(normalized);
    if (!key) return;

    const existing = mergedByKey.get(key);
    mergedByKey.set(
      key,
      existing
        ? normalizePropertyForPricingAssignment({ ...existing, ...normalized })
        : normalized
    );
  };

  groups.flat().forEach(addRecord);

  const bridgedByNickName = new Map();
  [...mergedByKey.values()].forEach((record) => {
    const nickName = String(record?.nickName || record?.NickName || "").trim().toLowerCase();
    if (!nickName) return;

    const existing = bridgedByNickName.get(nickName);
    bridgedByNickName.set(
      nickName,
      existing
        ? normalizePropertyForPricingAssignment({ ...existing, ...record })
        : record
    );
  });

  return [...bridgedByNickName.values()];
};

export const buildEntityZipMap = (entities = []) => {
  const map = {};

  (Array.isArray(entities) ? entities : []).forEach((entity) => {
    const entityId = getEntityRegistrationId(entity);
    const zip = getEntityAddressZipCode(entity);
    if (entityId && zip) {
      map[entityId] = zip;
    }
  });

  return map;
};

export const buildLotToEntityMap = (lots = []) => {
  const map = {};

  (Array.isArray(lots) ? lots : []).forEach((lot) => {
    const lotId = getParkingLotId(lot);
    const entityId = getEntityRegistrationId(lot);
    if (lotId && entityId) {
      map[lotId] = entityId;
    }
  });

  return map;
};

export const buildNickNameToEntityMap = (lots = []) => {
  const map = {};

  (Array.isArray(lots) ? lots : []).forEach((lot) => {
    const nickName = String(lot?.nickName || lot?.NickName || "").trim().toLowerCase();
    const entityId = getEntityRegistrationId(lot);
    if (nickName && entityId) {
      map[nickName] = entityId;
    }
  });

  return map;
};

export const resolveEntityIdForProperty = (
  property = {},
  lotToEntityMap = {},
  nickNameToEntityMap = {}
) =>
  getEntityRegistrationId(property) ||
  lotToEntityMap[getParkingLotId(property)] ||
  nickNameToEntityMap[String(property?.nickName || property?.NickName || "").trim().toLowerCase()] ||
  null;

export const getEntityZipForProperty = (
  property = {},
  entityZipMap = {},
  lotToEntityMap = {},
  nickNameToEntityMap = {}
) => {
  const entityId = resolveEntityIdForProperty(property, lotToEntityMap, nickNameToEntityMap);

  if (entityId && entityZipMap[entityId]) {
    return entityZipMap[entityId];
  }

  return "";
};

export const enrichPropertiesWithEntityZip = (
  properties = [],
  entityZipMap = {},
  lotToEntityMap = {},
  nickNameToEntityMap = {}
) =>
  (Array.isArray(properties) ? properties : []).map((property) => ({
    ...property,
    displayZipCode: getEntityZipForProperty(
      property,
      entityZipMap,
      lotToEntityMap,
      nickNameToEntityMap
    ),
  }));

export const propertyMatchesZipCode = (property = {}, zipCode, entityZipMap = {}) => {
  const normalizedZip = String(zipCode || "").trim();
  if (!normalizedZip) return false;

  const entityId = getEntityRegistrationId(property);
  const entityZip = entityId ? entityZipMap[entityId] : "";

  return (
    getPropertyZipCode(property) === normalizedZip ||
    String(property?.entityZip || "") === normalizedZip ||
    entityZip === normalizedZip
  );
};

export const findMatchingLot = (property = {}, allLots = []) => {
  const lots = Array.isArray(allLots) ? allLots : [];
  const lotId = getParkingLotId(property);
  if (lotId) {
    return lots.find((lot) => getParkingLotId(lot) === lotId) || null;
  }

  const nickName = String(property?.nickName ?? property?.NickName ?? "")
    .trim()
    .toLowerCase();
  const address = String(
    property?.parkingLotAddress ?? property?.ParkingLotAddress ?? ""
  )
    .trim()
    .toLowerCase();

  if (nickName && address) {
    const byNickAndAddress = lots.find((lot) => {
      const lotNick = String(lot?.nickName ?? lot?.NickName ?? "")
        .trim()
        .toLowerCase();
      const lotAddress = String(
        lot?.parkingLotAddress ??
          lot?.ParkingLotAddress ??
          lot?.parkingLotMailingAdderess ??
          ""
      )
        .trim()
        .toLowerCase();
      return lotNick === nickName && lotAddress === address;
    });
    if (byNickAndAddress) return byNickAndAddress;
  }

  if (nickName) {
    const matches = lots.filter((lot) => {
      const lotNick = String(lot?.nickName ?? lot?.NickName ?? "")
        .trim()
        .toLowerCase();
      return lotNick === nickName;
    });
    if (matches.length === 1) return matches[0];
  }

  if (address) {
    const byAddress = lots.find((lot) => {
      const lotAddress = String(
        lot?.parkingLotAddress ??
          lot?.ParkingLotAddress ??
          lot?.parkingLotMailingAdderess ??
          ""
      )
        .trim()
        .toLowerCase();
      return (
        lotAddress &&
        (lotAddress === address ||
          lotAddress.includes(address) ||
          address.includes(lotAddress))
      );
    });
    if (byAddress) return byAddress;
  }

  return null;
};

export const resolvePropertyRecordForAssignment = (property = {}, allLots = []) => {
  const matched = findMatchingLot(property, allLots);
  if (!matched) return property;

  return {
    ...matched,
    ...property,
    parking_Lot_ID: getParkingLotId(property) || getParkingLotId(matched),
    parkingLotID: getParkingLotId(property) || getParkingLotId(matched),
    parkingLotId: getParkingLotId(property) || getParkingLotId(matched),
    nickName: property?.nickName || property?.NickName || matched?.nickName || matched?.NickName,
    parkingLotAddress:
      property?.parkingLotAddress ||
      property?.ParkingLotAddress ||
      matched?.parkingLotAddress ||
      matched?.ParkingLotAddress,
    entityRegistrationID:
      getEntityRegistrationId(property) || getEntityRegistrationId(matched),
  };
};
